import { RecentChatQueryOption } from "@/apis/query/chat";
import { Pending } from "@/components/Pending";
import { useAdvancedInfiniteQuery } from "@/hooks/useAdvancedInfiniteQuery";
import { useRouter } from "@/locales/routing";
import { useScrollElementContext } from "@/provider/ScrollElementProvider";
import { PropsWithClassname } from "@/types";
import { ErrorConfirm } from "@/ui/Confirm/ErrorConfirm";
import { Empty } from "@/ui/Empty/Empty";
import { Avatar, cn, SharedSelection, useDisclosure } from "@heroui/react";
import { flatten } from "lodash";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDeleteChat } from "../../_hooks/useDeleteChat";
import { ChatListBox } from "./ChatListBox";
import { useList } from "./useList";
import SetNameModal from "./SetNameModal";

export const UserList = ({ className }: PropsWithClassname) => {
  const router = useRouter()
  const { container } = useScrollElementContext()

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isPending,
    updateCb,
    refetch
  } = useAdvancedInfiniteQuery(RecentChatQueryOption)

  useEffect(() => {
    if (!data) refetch
  }, [data])

  const users = useMemo(() => {
    return flatten(data?.pages?.
      map(page => page?.
        map(item => {
          // 先把 chat_record 取成确定的数组，避免 item.chat_record.length 在异常数据下抛
          // "Cannot read properties of undefined (reading 'length')"
          const records = Array.isArray(item?.chat_record) ? item.chat_record : []
          const lastRecord = records[records.length - 1]
          return ({
            name: item.session_name,
            avatar: item.chat_robot?.robot_info?.avatar,
            chat_record: item.chat_record,
            robotsId: item.chat_robot?.robot_info?.robots_id,
            scene: lastRecord?.translate_content
              || lastRecord?.content
              || (lastRecord?.image_url ? '[Image]' : ''),
            pinned: !!item.pinned,
            id: String(item.id),
            chat_id: item.chat_id
          })
        }
        ) ?? []) || [])
  }, [data])

  const {
    values,
    onPinned
  } = useList({ users, updateCb })


  const activeId = useRef<number>(null!)
  // delete
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure()
  const t = useTranslations()
  const { onDeleteChat } = useDeleteChat(RecentChatQueryOption as any, {
    onSuccess: onClose
  })

  const onDelete = (id) => {
    activeId.current = Number(id)
    onOpen()
  }

  // setName 
  const setNameModal = useDisclosure()
  const onSetName = (id) => {
    activeId.current = Number(id)
    setNameModal.onOpen()
  }


  return (
    <>
      <div className={cn("mt-[20px]", className)}>
        {isPending || <ChatListBox
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          childSelector="li"
          list={users}
          emptyContent={<Empty />}
          selectedKeys={values}
          onSelectionChange={(val: SharedSelection) => {
            const chatInfo = users.find((item: any) => item?.id === val.currentKey) as any;
            if (!chatInfo) return;
            router.push(`/chats/${val.currentKey}/${chatInfo.chat_id}`);
          }}
          onSetName={onSetName}
          scrollCotnainer={container}
          avatar={
            item => <Avatar
              alt={item.name}
              className="flex-shrink-0"
              size="md"
              src={item.avatar} />}

          onPinned={onPinned}
          onDelete={onDelete}
          data={data as any}
        />}

        {isFetching && <Pending size="sm" className="mt-2" />}
      </div>

      <ErrorConfirm
        title={t('chat.deleteChat.title')}
        message={t('chat.deleteChat.alert')}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onConfirm={() => onDeleteChat([activeId.current],)}
      />

      <SetNameModal
        isOpen={setNameModal.isOpen}
        onClose={setNameModal.onClose}

        onOpenChange={setNameModal.onOpenChange}
        defaultName={users.find(item => { return Number(item?.id) === activeId.current })?.name}
        sessionId={activeId.current}
      />

    </>

  )
}